import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, IconButton, Link } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import FolderIcon from '@mui/icons-material/Folder';
import KeyIcon from '@mui/icons-material/VpnKey';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import {
  initTutorialProgress,
  startTutorial,
  setTutorialStep,
  completeStep,
  completeTutorial,
  dismissTutorial,
  TUTORIAL_IDS,
} from '../utils/tutorialProgress';

// Provider links for getting API keys
const PROVIDER_LINKS: Record<string, { name: string; url: string; color: string }> = {
  openai: { name: 'OpenAI', url: 'https://platform.openai.com/api-keys', color: '#10A37F' },
  anthropic: { name: 'Anthropic', url: 'https://console.anthropic.com/settings/keys', color: '#D4A27F' },
  google: { name: 'Google AI', url: 'https://aistudio.google.com/app/apikey', color: '#4285F4' },
  mistral: { name: 'Mistral AI', url: 'https://console.mistral.ai/api-keys/', color: '#FF7000' },
  cohere: { name: 'Cohere', url: 'https://dashboard.cohere.com/api-keys', color: '#39594D' },
};

interface TutorialSubStep {
  id: string;
  instruction: string;
  targetSelector: string;
  highlightText?: string;
  waitForAction?: boolean;
  hoverAdvance?: boolean; // If true, advance on hover (for sidebar menus)
}

interface TutorialStep {
  id: string;
  title: string;
  description: string;
  targetSelector: string;
  targetRoute: string;
  icon: React.ReactNode;
  actionLabel: string;
  completedLabel: string;
  checkCompletion: () => boolean;
  subSteps?: TutorialSubStep[];
  showProviderLinks?: boolean;
  infoMessage?: string;
}

interface CloudGatewayTutorialProps {
  isActive: boolean;
  onClose: () => void;
  onComplete: () => void;
  currentStep: number;
  onStepChange: (step: number) => void;
  aiConfigsCount: number;
  reposCount: number;
  hasApiKey: boolean;
  hasFirstTokenConsumed?: boolean;
}

const CloudGatewayTutorial: React.FC<CloudGatewayTutorialProps> = ({
  isActive,
  onClose,
  onComplete,
  currentStep,
  onStepChange,
  aiConfigsCount,
  reposCount,
  hasApiKey,
  hasFirstTokenConsumed = false,
}) => {
  const [highlightRect, setHighlightRect] = useState<DOMRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [subStepIndex, setSubStepIndex] = useState(0);
  const [showNavigationPrompt, setShowNavigationPrompt] = useState(false);
  const [elementNotFound, setElementNotFound] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const lastAdvanceTimeRef = useRef<number>(0);

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize tutorial progress tracking in localStorage
  useEffect(() => {
    const stepIds = ['add-ai', 'create-repo', 'generate-key', 'first-request'];
    initTutorialProgress(TUTORIAL_IDS.CLOUD_GATEWAY, stepIds);
  }, []);

  // Track when tutorial becomes active
  useEffect(() => {
    if (isActive) {
      startTutorial(TUTORIAL_IDS.CLOUD_GATEWAY);
    }
  }, [isActive]);

  // Track step changes in localStorage
  useEffect(() => {
    if (isActive) {
      setTutorialStep(TUTORIAL_IDS.CLOUD_GATEWAY, currentStep);
    }
  }, [isActive, currentStep]);

  // Progressive tutorial with sub-steps - each sub-step highlights a different element
  // Sub-steps advance automatically when the user performs the action (e.g., clicks, navigates)
  const steps: TutorialStep[] = [
    {
      id: 'add-ai',
      title: 'Configure an AI Provider',
      description: 'Let\'s set up your first AI provider. You\'ll need an API key from a provider like OpenAI, Anthropic, or Google.',
      targetSelector: '[data-tutorial="menu-ai"]', // Start with sidebar AI menu
      targetRoute: '/workspace/home',
      icon: <SmartToyIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Hover on "AI" in the sidebar',
      completedLabel: 'AI Provider configured!',
      checkCompletion: () => aiConfigsCount > 0,
      showProviderLinks: false,
      infoMessage: '',
      subSteps: [
        { id: 'hover-menu-ai', instruction: 'Hover on "AI" in the sidebar', targetSelector: '[data-tutorial="menu-ai"]', hoverAdvance: true },
        { id: 'click-menu-llm', instruction: 'Click on "AI LLM" to open the LLM configuration page', targetSelector: '[data-tutorial="menu-llm"]' },
        { id: 'click-add-ai', instruction: 'Click the + button to add a provider', targetSelector: '[data-tutorial="add-ai"]' },
        { id: 'select-provider', instruction: 'Select a provider (e.g., OpenAI)', targetSelector: '[data-tutorial="provider-openai"]' },
        { id: 'fill-name', instruction: 'Enter a configuration name', targetSelector: '[data-tutorial="ai-config-name"]' },
        { id: 'fill-api-key', instruction: 'Enter your API key', targetSelector: '[data-tutorial="ai-api-key"]' },
        { id: 'submit-form', instruction: 'Click to save your configuration', targetSelector: '[data-tutorial="ai-submit"]', waitForAction: true },
      ],
    },
    {
      id: 'create-repo',
      title: 'Create a Repository',
      description: 'Repositories organize your AI configurations and provide API endpoints. Create one to group your resources.',
      targetSelector: '[data-tutorial="menu-repositories"]',
      targetRoute: '/workspace/home',
      icon: <FolderIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Hover on "Repositories" in the sidebar',
      completedLabel: 'Repository created!',
      checkCompletion: () => reposCount > 0,
      subSteps: [
        { id: 'hover-menu-repositories', instruction: 'Hover on "Repositories" in the sidebar', targetSelector: '[data-tutorial="menu-repositories"]', hoverAdvance: true },
        { id: 'click-menu-inventory', instruction: 'Click on "Inventory" to open the repositories page', targetSelector: '[data-tutorial="menu-inventory"]' },
        { id: 'click-add-repo', instruction: 'Click the + button to add a repository', targetSelector: '[data-tutorial="add-repo"]' },
        { id: 'fill-repo-name', instruction: 'Enter a repository name', targetSelector: '[data-tutorial="repo-name"]' },
        { id: 'submit-repo', instruction: 'Click "Finish setup" to create your repository', targetSelector: '[data-tutorial="repo-submit"]', waitForAction: true },
      ],
    },
    {
      id: 'generate-key',
      title: 'Generate an API Key',
      description: 'Create a Flashback API key to access your AI services. This key authenticates your applications with the Gateway.',
      targetSelector: '[data-tutorial="menu-repositories"]',
      targetRoute: '/workspace/home',
      icon: <KeyIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Hover on "Repositories" in the sidebar',
      completedLabel: 'API Key generated!',
      checkCompletion: () => hasApiKey,
      infoMessage: '',
      subSteps: [
        { id: 'hover-menu-repositories', instruction: 'Hover on "Repositories" in the sidebar', targetSelector: '[data-tutorial="menu-repositories"]', hoverAdvance: true },
        { id: 'click-menu-api-keys', instruction: 'Click on "API Keys" to open the API keys page', targetSelector: '[data-tutorial="menu-api-keys"]' },
        { id: 'click-ai-tab', instruction: 'Click on "AI LLMs" tab', targetSelector: '[data-tutorial="tab-ai-llms"]' },
        { id: 'select-repo', instruction: 'Select a repository', targetSelector: '[data-tutorial="select-repo"]' },
        { id: 'click-generate', instruction: 'Click "Create API Key"', targetSelector: '[data-tutorial="generate-key"]', waitForAction: true },
      ],
    },
    {
      id: 'first-request',
      title: 'Make Your First Request',
      description: 'Your setup is complete! Copy the code example below and use your Flashback API key to make requests through the Gateway.',
      targetSelector: '[data-tutorial="code-example"]',
      targetRoute: '/workspace/home',
      icon: <CodeIcon sx={{ fontSize: 24, color: 'white' }} />,
      actionLabel: 'Copy the code example and try it!',
      completedLabel: 'Congratulations! Tutorial completed!',
      checkCompletion: () => hasFirstTokenConsumed,
      infoMessage: 'Replace YOUR_API_KEY with your actual Flashback Gateway API key. The Gateway will route your request to the configured provider.',
      subSteps: [
        { id: 'copy-example', instruction: 'Copy the code example below', targetSelector: '[data-tutorial="code-example"]' },
      ],
    },
  ];

  const currentStepData = steps[currentStep];
  const isStepCompleted = currentStepData?.checkCompletion() || false;
  const isLastStep = currentStep === steps.length - 1;
  
  // Get current sub-step data
  const currentSubSteps = currentStepData?.subSteps || [];
  const currentSubStep = currentSubSteps[subStepIndex];
  const activeTargetSelector = currentSubStep?.targetSelector || currentStepData?.targetSelector;
  
  // Track which step we've already navigated for (to avoid re-navigation loops)
  const navigatedStepRef = useRef<number>(-1);

  // Soft navigation - show prompt instead of forcing redirect
  useEffect(() => {
    if (isActive && currentStepData && navigatedStepRef.current !== currentStep) {
      navigatedStepRef.current = currentStep;
      // Check if we need to navigate
      if (location.pathname !== currentStepData.targetRoute) {
        // Show a navigation prompt instead of forcing redirect
        setShowNavigationPrompt(true);
      } else {
        setShowNavigationPrompt(false);
      }
    }
  }, [isActive, currentStep, currentStepData, location.pathname]);
  
  // Update navigation prompt when location changes
  useEffect(() => {
    if (isActive && currentStepData) {
      setShowNavigationPrompt(location.pathname !== currentStepData.targetRoute);
    }
  }, [location.pathname, isActive, currentStepData]);
  
  // Reset states when tutorial closes
  useEffect(() => {
    if (!isActive) {
      navigatedStepRef.current = -1;
      setSubStepIndex(0);
      setShowNavigationPrompt(false);
    }
  }, [isActive]);
  
  // Reset sub-step when main step changes
  useEffect(() => {
    setSubStepIndex(0);
  }, [currentStep]);
  
  // Handle navigation button click (soft navigation)
  const handleNavigateToStep = () => {
    if (currentStepData) {
      navigate(currentStepData.targetRoute);
      setShowNavigationPrompt(false);
    }
  };

  // Find and highlight target element - uses activeTargetSelector which changes based on sub-step
  const updateHighlight = useCallback(() => {
    if (!isActive || !activeTargetSelector) return;

    const target = document.querySelector(activeTargetSelector);
    if (target) {
      const rect = target.getBoundingClientRect();
      setHighlightRect(rect);

      // Position tooltip - adjacent to highlighted element for clarity
      const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
      const tooltipWidth = 300;
      
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const margin = 16;
      const gap = 12; // Gap between tooltip and highlighted element

      // Check if there's a modal/dialog open (MUI Dialog or HeadlessUI)
      const hasOpenModal = document.querySelector('[role="dialog"], [role="presentation"], .MuiDialog-root, .MuiModal-root');
      
      let top: number;
      let left: number;
      
      // Check element position
      const isSidebar = rect.left < 300;
      const isInModal = hasOpenModal && hasOpenModal.contains(target);
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      if (isSidebar && !isInModal) {
        // Target in sidebar: position tooltip to the RIGHT of the element
        top = rect.top;
        left = rect.right + gap;
        
        // If tooltip goes off screen, position it at center-right
        if (left + tooltipWidth > viewportWidth - margin) {
          left = viewportWidth - tooltipWidth - margin;
        }
        // Vertically align with element, but keep within viewport
        if (top + tooltipHeight > viewportHeight - margin) {
          top = viewportHeight - tooltipHeight - margin;
        }
      } else if (isInModal) {
        // Target is inside a modal: position tooltip OUTSIDE the modal
        // Prefer bottom-right corner to avoid overlapping the modal content
        const modalRect = hasOpenModal.getBoundingClientRect();
        
        // Try right of modal first
        if (modalRect.right + gap + tooltipWidth < viewportWidth - margin) {
          left = modalRect.right + gap;
          top = modalRect.top + 50; // Slightly below modal top
        } 
        // Try left of modal
        else if (modalRect.left - gap - tooltipWidth > margin) {
          left = modalRect.left - tooltipWidth - gap;
          top = modalRect.top + 50;
        }
        // Fallback: bottom-right corner of viewport
        else {
          top = viewportHeight - tooltipHeight - margin - 60;
          left = viewportWidth - tooltipWidth - margin;
        }
      } else {
        // Target is in main content area: position adjacent to element
        // Try to position to the RIGHT of the element first
        if (rect.right + gap + tooltipWidth < viewportWidth - margin) {
          left = rect.right + gap;
          top = rect.top;
        }
        // Try LEFT of element
        else if (rect.left - gap - tooltipWidth > margin + 300) { // Account for sidebar
          left = rect.left - tooltipWidth - gap;
          top = rect.top;
        }
        // Try BELOW element
        else if (rect.bottom + gap + tooltipHeight < viewportHeight - margin) {
          left = Math.max(margin + 300, Math.min(rect.left, viewportWidth - tooltipWidth - margin));
          top = rect.bottom + gap;
        }
        // Try ABOVE element
        else if (rect.top - gap - tooltipHeight > margin) {
          left = Math.max(margin + 300, Math.min(rect.left, viewportWidth - tooltipWidth - margin));
          top = rect.top - tooltipHeight - gap;
        }
        // Fallback: bottom-right corner
        else {
          top = viewportHeight - tooltipHeight - margin - 60;
          left = viewportWidth - tooltipWidth - margin;
        }
      }

      // Final bounds check
      if (top < margin) top = margin;
      if (top + tooltipHeight > viewportHeight - margin) {
        top = viewportHeight - tooltipHeight - margin;
      }
      if (left < margin) left = margin;
      if (left + tooltipWidth > viewportWidth - margin) {
        left = viewportWidth - tooltipWidth - margin;
      }

      setTooltipPosition({ top, left });
      setElementNotFound(false);
    } else {
      setHighlightRect(null);
      setElementNotFound(true);
      
      // Position tooltip in bottom-right corner when target not found
      const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
      const tooltipWidth = 300;
      
      const top = window.innerHeight - tooltipHeight - 12 - 80;
      const left = window.innerWidth - tooltipWidth - 12;
      
      setTooltipPosition({ 
        top: Math.max(12, top), 
        left: Math.max(12, left) 
      });
    }
  }, [isActive, activeTargetSelector, isStepCompleted, showNavigationPrompt, subStepIndex]);
  
  // NO auto-advance - steps only advance via user click/hover actions
  // This ensures no steps are ever skipped automatically
  
  // Listen for clicks on highlighted elements to advance sub-steps
  // Hover advances only for substeps with hoverAdvance: true (sidebar menus)
  useEffect(() => {
    if (!isActive || !activeTargetSelector) return;
    
    // Debounce: prevent multiple advances within 800ms
    const canAdvance = () => {
      const now = Date.now();
      if (now - lastAdvanceTimeRef.current < 800) {
        return false;
      }
      lastAdvanceTimeRef.current = now;
      return true;
    };
    
    const handleClick = (e: MouseEvent) => {
      const target = document.querySelector(activeTargetSelector);
      if (target && (target === e.target || target.contains(e.target as Node))) {
        // User clicked on the highlighted element
        if (currentSubSteps.length > 0 && subStepIndex < currentSubSteps.length - 1) {
          if (canAdvance()) {
            console.log('[Tutorial] Click advance from sub-step', subStepIndex, 'to', subStepIndex + 1);
            setSubStepIndex(prev => prev + 1);
          }
        }
      }
    };
    
    const handleHover = (e: MouseEvent) => {
      // Only advance on hover if current substep has hoverAdvance: true
      if (!currentSubStep?.hoverAdvance) return;
      
      const target = document.querySelector(activeTargetSelector);
      if (target && (target === e.target || target.contains(e.target as Node))) {
        // User hovered on the highlighted sidebar element
        if (currentSubSteps.length > 0 && subStepIndex < currentSubSteps.length - 1) {
          if (canAdvance()) {
            console.log('[Tutorial] Hover advance from sub-step', subStepIndex, 'to', subStepIndex + 1);
            setSubStepIndex(prev => prev + 1);
          }
        }
      }
    };
    
    document.addEventListener('click', handleClick, true);
    document.addEventListener('mouseenter', handleHover, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('mouseenter', handleHover, true);
    };
  }, [isActive, activeTargetSelector, currentSubSteps.length, subStepIndex, currentSubStep]);

  useEffect(() => {
    updateHighlight();
    window.addEventListener('resize', updateHighlight);
    window.addEventListener('scroll', updateHighlight);

    // Update periodically to catch dynamic elements
    const interval = setInterval(updateHighlight, 500);

    return () => {
      window.removeEventListener('resize', updateHighlight);
      window.removeEventListener('scroll', updateHighlight);
      clearInterval(interval);
    };
  }, [updateHighlight]);

  // Check if step is completed and auto-advance + navigate to next step's page
  useEffect(() => {
    if (isActive && isStepCompleted && !isLastStep) {
      // Mark step as completed in localStorage
      const stepId = steps[currentStep]?.id;
      if (stepId) {
        completeStep(TUTORIAL_IDS.CLOUD_GATEWAY, stepId);
      }
      // Small delay before auto-advancing
      const timer = setTimeout(() => {
        const nextStep = steps[currentStep + 1];
        // Navigate to the next step's target route
        if (nextStep?.targetRoute && location.pathname !== nextStep.targetRoute) {
          navigate(nextStep.targetRoute);
        }
        onStepChange(currentStep + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isActive, isStepCompleted, isLastStep, currentStep, onStepChange, navigate, location.pathname, steps]);

  const handleNext = () => {
    if (isLastStep) {
      // Mark tutorial as completed in localStorage
      completeTutorial(TUTORIAL_IDS.CLOUD_GATEWAY);
      onComplete();
    } else if (isStepCompleted) {
      onStepChange(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      onStepChange(currentStep - 1);
    }
  };

  const handleSkip = () => {
    // Mark tutorial as dismissed in localStorage
    dismissTutorial(TUTORIAL_IDS.CLOUD_GATEWAY);
    onClose();
  };

  if (!isActive) return null;

  // Always use light mode - no blocking overlay, just highlight ring
  // This provides a non-intrusive guided experience
  const useLightMode = true;

  // Calculate the 4 overlay regions around the highlighted element
  const padding = 8;
  const holeTop = highlightRect ? highlightRect.top - padding : 0;
  const holeLeft = highlightRect ? highlightRect.left - padding : 0;
  const holeWidth = highlightRect ? highlightRect.width + padding * 2 : 0;
  const holeHeight = highlightRect ? highlightRect.height + padding * 2 : 0;
  const holeRight = holeLeft + holeWidth;
  const holeBottom = holeTop + holeHeight;

  // Backdrop filter for a premium glass effect - only used in full mode
  const overlayStyle = {
    position: 'fixed',
    bgcolor: useLightMode ? 'transparent' : 'rgba(0, 0, 0, 0.4)',
    backdropFilter: useLightMode ? 'none' : 'blur(4px)',
    zIndex: 9998,
    transition: 'all 0.3s ease',
    pointerEvents: useLightMode ? 'none' as const : 'auto' as const,
  };

  return (
    <>
      {/* Light mode: just highlight ring when element is found, no blocking overlays */}
      {highlightRect && (
        <Box
          sx={{
            position: 'fixed',
            top: holeTop,
            left: holeLeft,
            width: holeWidth,
            height: holeHeight,
            borderRadius: '12px',
            boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)',
            animation: 'guided-pulse-light 2s ease-in-out infinite',
            '@keyframes guided-pulse-light': {
              '0%': { boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)' },
              '50%': { boxShadow: '0 0 0 6px rgba(97, 123, 255, 0.3), 0 0 30px rgba(97, 123, 255, 0.4)' },
              '100%': { boxShadow: '0 0 0 3px rgba(97, 123, 255, 0.5), 0 0 20px rgba(97, 123, 255, 0.3)' },
            },
            pointerEvents: 'none',
            zIndex: 9998,
          }}
        />
      )}

      {/* Tooltip card - always compact for non-intrusive guide */}
      <Box
        ref={tooltipRef}
        sx={{
          position: 'fixed',
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: 300,
          bgcolor: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
          zIndex: 9999,
          overflow: 'hidden',
          transition: 'top 0.3s cubic-bezier(0.4, 0, 0.2, 1), left 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
          opacity: tooltipPosition.top === 0 ? 0 : 1,
          border: '1px solid #E5E7EB',
        }}
      >
        {/* Header - Compact */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E5E7EB',
            bgcolor: '#F9FAFB',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                bgcolor: '#617BFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: 'white' }}>
                {currentStep + 1}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#101828' }}>
              {currentStepData?.title} {currentSubSteps.length > 1 && `(${subStepIndex + 1}/${currentSubSteps.length})`}
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={handleSkip} 
            sx={{ 
              p: 0.5,
              color: '#9CA3AF', 
              '&:hover': { color: '#667085', bgcolor: '#F3F4F6' } 
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Content - Compact */}
        <Box sx={{ px: 2, py: 1.5 }}>
          {/* Action instruction */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: '8px',
              bgcolor: isStepCompleted ? '#F0FDF4' : elementNotFound ? '#FEF3C7' : '#F5F7FF',
              border: '1px solid',
              borderColor: isStepCompleted ? '#BBF7D0' : elementNotFound ? '#FCD34D' : '#E0E7FF',
            }}
          >
            {isStepCompleted ? (
              <CheckCircleIcon sx={{ fontSize: 18, color: '#10B981' }} />
            ) : elementNotFound ? (
              <InfoOutlinedIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
            ) : (
              <TouchAppIcon sx={{ fontSize: 18, color: '#617BFF' }} />
            )}
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: isStepCompleted ? '#15803D' : elementNotFound ? '#92400E' : '#374151',
                flex: 1,
              }}
            >
              {isStepCompleted 
                ? currentStepData?.completedLabel 
                : elementNotFound
                  ? 'Element not visible. Navigate to the correct page or hover on the sidebar menu.'
                  : showNavigationPrompt
                    ? `Navigate to ${currentStepData?.title}`
                    : currentSubStep?.instruction || currentStepData?.actionLabel
              }
            </Typography>
          </Box>
          
          {/* Navigation Button - only when needed */}
          {showNavigationPrompt && !isStepCompleted && (
            <Button
              fullWidth
              variant="contained"
              size="small"
              onClick={handleNavigateToStep}
              sx={{
                mt: 1.5,
                textTransform: 'none',
                bgcolor: '#617BFF',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                py: 1,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#5068e0', boxShadow: 'none' },
              }}
            >
              Take me there
            </Button>
          )}
        </Box>

        {/* Footer - compact with progress */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <Typography
            onClick={handleSkip}
            sx={{
              fontSize: '12px',
              color: '#9CA3AF',
              cursor: 'pointer',
              '&:hover': { color: '#667085' },
            }}
          >
            Skip
          </Typography>

          {/* Progress dots */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {steps.map((_, idx) => (
              <Box
                key={idx}
                sx={{
                  width: idx === currentStep ? 16 : 6,
                  height: 6,
                  borderRadius: 3,
                  bgcolor: idx <= currentStep ? '#617BFF' : '#E5E7EB',
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>

          {isStepCompleted ? (
            <Box
              onClick={handleNext}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#617BFF',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { color: '#4A67E3' },
              }}
            >
              {isLastStep ? 'Done' : 'Next'}
              {!isLastStep && <ArrowForwardIcon sx={{ fontSize: 14 }} />}
            </Box>
          ) : (
            <Typography sx={{ fontSize: '12px', color: '#9CA3AF' }}>
              {currentStep + 1}/{steps.length}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CloudGatewayTutorial;
